import {
  AfterViewChecked,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { GematikTourService } from '../../services/gematik-tour.service';
import { GematikTourState, GematikTourStep } from '../../models/gematik-tour.model';
import {
  GEMATIK_TOUR_DEFAULT_LABELS,
  GEMATIK_TOUR_LABELS,
  GematikTourLabels,
} from '../../services/gematik-tour-labels.token';

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

@Component({
  selector: 'gematik-tour-overlay',
  templateUrl: './gematik-tour-overlay.component.html',
  styleUrls: ['./gematik-tour-overlay.component.scss'],
  standalone: false,
})
export class GematikTourOverlayComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('popover') popoverRef?: ElementRef<HTMLElement>;

  state: GematikTourState = { active: false, currentStep: 0, steps: [] };
  spotlightRect = { x: 0, y: 0, width: 0, height: 0 };
  popoverTop = 0;
  popoverLeft = 0;
  popoverPosition: 'top' | 'bottom' = 'bottom';
  displayStepIndex = 0;
  contentLoading = false;
  overlayReady = false;

  private sub!: Subscription;
  private originalParent: Node | null = null;
  private elementToFocusOnOpen: HTMLElement | null = null;
  private lastFocusedElement: HTMLElement | null = null;
  private pendingFocus = false;
  private lastDirection: 1 | -1 = 1;
  private positionToken = 0;
  // Set when the step changes; cleared by whichever positionSpotlight() pass settles last.
  // Kept as state (not a call argument) because scroll/resize-triggered passes supersede
  // the step-change pass via positionToken and would otherwise drop the content swap.
  private pendingStepChange = false;

  constructor(
    private tourService: GematikTourService,
    private elRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    @Optional() @Inject(GEMATIK_TOUR_LABELS) private customLabels: GematikTourLabels | null,
  ) {}

  get labels(): GematikTourLabels {
    return this.customLabels ?? GEMATIK_TOUR_DEFAULT_LABELS;
  }

  ngOnInit(): void {
    // Move the host element to document.body to escape any stacking context
    this.originalParent = this.elRef.nativeElement.parentNode;
    this.renderer.appendChild(document.body, this.elRef.nativeElement);

    this.sub = this.tourService.tour$.subscribe(s => {
      const wasActive = this.state.active;
      const prevStepIndex = this.state.currentStep;
      this.state = s;
      if (s.active) {
        if (!wasActive) {
          this.lastFocusedElement = document.activeElement as HTMLElement | null;
          this.displayStepIndex = s.currentStep;
          this.contentLoading = false;
          this.overlayReady = false;
          this.lastDirection = 1;
        } else if (prevStepIndex !== s.currentStep) {
          this.contentLoading = true;
        }
        this.pendingFocus = true;
        this.pendingStepChange = true;
        setTimeout(() => this.positionSpotlight(), 0);
      } else if (wasActive) {
        this.pendingStepChange = false;
        this.lastFocusedElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.lastFocusedElement?.focus?.({ preventScroll: true });
        this.lastFocusedElement = null;
      }
    });
  }

  ngAfterViewChecked(): void {
    if (this.pendingFocus && this.popoverRef) {
      this.pendingFocus = false;
      this.popoverRef.nativeElement.focus({ preventScroll: true });
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    if (this.originalParent) {
      this.renderer.appendChild(this.originalParent, this.elRef.nativeElement);
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.state.active) {
      this.positionSpotlight();
    }
  }

  @HostListener('window:scroll')
  onScroll(): void {
    if (this.state.active) {
      this.positionSpotlight();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.state.active) {
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.onEnd();
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.onNext();
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.onPrev();
      return;
    }
    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  get currentStep(): GematikTourStep | null {
    return this.state.steps[this.displayStepIndex] ?? null;
  }

  private get targetStep(): GematikTourStep | null {
    return this.state.steps[this.state.currentStep] ?? null;
  }

  get isLastStep(): boolean {
    return this.displayStepIndex === this.state.steps.length - 1;
  }

  private get resolvableSteps(): boolean[] {
    return this.state.steps.map(step => !!document.querySelector(step.selector));
  }

  get displayStepNumber(): number {
    return this.resolvableSteps.slice(0, this.displayStepIndex + 1).filter(Boolean).length;
  }

  get totalVisibleSteps(): number {
    return this.resolvableSteps.filter(Boolean).length;
  }

  onNext(): void {
    this.pendingFocus = true;
    this.lastDirection = 1;
    this.tourService.next();
  }

  onPrev(): void {
    this.pendingFocus = true;
    this.lastDirection = -1;
    this.tourService.prev();
  }

  onEnd(): void {
    this.tourService.skip();
  }

  onBackdropClick(): void {}

  private trapFocus(event: KeyboardEvent): void {
    const container = this.popoverRef?.nativeElement;
    if (!container) {
      return;
    }
    const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    if (focusable.length === 0) {
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    } else if (!container.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  }

  private positionSpotlight(): void {
    const step = this.targetStep;
    if (!step) return;

    const el = document.querySelector(step.selector);
    if (!el) {
      const before = this.state.currentStep;
      if (this.lastDirection === -1) {
        this.tourService.prev();
      } else {
        this.tourService.next();
      }
      if (this.tourService.state.currentStep === before) {
        this.contentLoading = false;
      }
      return;
    }

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const token = ++this.positionToken;
    this.waitForScrollSettle(el, () => {
      if (token !== this.positionToken) return;

      const rect = el.getBoundingClientRect();
      this.spotlightRect = {
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      };

      const popoverWidth = 380;
      const popoverHeight = this.popoverRef?.nativeElement.offsetHeight ?? 180;
      const preferredPosition = step.position ?? 'bottom';

      if (preferredPosition === 'top' || rect.bottom + popoverHeight + 16 > window.innerHeight) {
        this.popoverPosition = 'top';
        this.popoverTop = Math.max(8, rect.top - popoverHeight - 16);
      } else {
        this.popoverPosition = 'bottom';
        this.popoverTop = rect.bottom + 16;
      }

      this.popoverLeft = Math.max(
        8,
        Math.min(rect.left + rect.width / 2 - popoverWidth / 2, window.innerWidth - popoverWidth - 8),
      );

      if (this.pendingStepChange) {
        this.pendingStepChange = false;
        this.displayStepIndex = this.state.currentStep;
        this.contentLoading = false;
      }

      this.overlayReady = true;
    });
  }

  // scrollIntoView({behavior: 'smooth'}) can take longer than any fixed delay to
  // settle (e.g. returning to a short step after a tall one that scrolled far away),
  // so wait for the element's position to stop changing before measuring it.
  private waitForScrollSettle(el: Element, callback: () => void): void {
    let lastRect = el.getBoundingClientRect();
    let stableFrames = 0;
    let totalFrames = 0;
    const maxFrames = 90;

    const check = (): void => {
      totalFrames++;
      const rect = el.getBoundingClientRect();
      const moved = Math.abs(rect.top - lastRect.top) > 0.5 || Math.abs(rect.left - lastRect.left) > 0.5;
      lastRect = rect;

      if (!moved) {
        stableFrames++;
      } else {
        stableFrames = 0;
      }

      if (stableFrames >= 3 || totalFrames >= maxFrames) {
        callback();
      } else {
        requestAnimationFrame(check);
      }
    };

    requestAnimationFrame(check);
  }
}
